/**
 * In-house monitoring service — error tracking, performance observability, and structured logging.
 * Works in both demo mode (localStorage) and Firebase mode (Firestore).
 */
import { isDemoMode, db } from '../firebase';

// ── Types ──

export type MonitoringEventType = 'error' | 'performance' | 'action' | 'navigation';
export type MonitoringEventSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface MonitoringEvent {
  id: string;
  type: MonitoringEventType;
  severity: MonitoringEventSeverity;
  message: string;
  timestamp: number; // epoch ms
  /** User context at the time of the event */
  user?: { uid: string; email: string; role: string } | null;
  /** Additional structured metadata */
  meta?: Record<string, unknown>;
  /** Error-specific: stack trace */
  stack?: string;
  /** Error-specific: breadcrumbs leading up to the error */
  breadcrumbs?: Breadcrumb[];
  /** Performance-specific: duration in ms */
  duration?: number;
  /** Navigation / action: page URL */
  url?: string;
}

export interface Breadcrumb {
  type: 'navigation' | 'action' | 'network' | 'console' | 'error';
  message: string;
  timestamp: number;
  meta?: Record<string, unknown>;
}

export interface PerformanceMetrics {
  /** First Contentful Paint */
  fcp?: number;
  /** Largest Contentful Paint */
  lcp?: number;
  /** Cumulative Layout Shift */
  cls?: number;
  /** Interaction to Next Paint */
  inp?: number;
  /** Time to First Byte */
  ttfb?: number;
  /** Full page load time */
  pageLoad?: number;
  timestamp: number;
  url: string;
}

// ── Storage keys ──

const EVENTS_KEY = 'monitoring_events';
const METRICS_KEY = 'monitoring_metrics';
const MAX_EVENTS = 500;
const MAX_BREADCRUMBS = 25;

// ── Internal state ──

let _userContext: MonitoringEvent['user'] = null;
let _breadcrumbs: Breadcrumb[] = [];
let _initialized = false;

// ── Helpers ──

const generateId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const getStoredEvents = (): MonitoringEvent[] => {
  try {
    const raw = localStorage.getItem(EVENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const storeEvent = (event: MonitoringEvent) => {
  const events = getStoredEvents();
  events.unshift(event);
  // Cap storage to prevent unbounded growth
  if (events.length > MAX_EVENTS) events.length = MAX_EVENTS;
  try {
    localStorage.setItem(EVENTS_KEY, JSON.stringify(events));
  } catch {
    // Storage full — trim aggressively
    events.length = Math.floor(MAX_EVENTS / 2);
    localStorage.setItem(EVENTS_KEY, JSON.stringify(events));
  }
};

const getStoredMetrics = (): PerformanceMetrics[] => {
  try {
    const raw = localStorage.getItem(METRICS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const storeMetrics = (metrics: PerformanceMetrics) => {
  const all = getStoredMetrics();
  all.unshift(metrics);
  if (all.length > 100) all.length = 100;
  try {
    localStorage.setItem(METRICS_KEY, JSON.stringify(all));
  } catch {
    all.length = 50;
    localStorage.setItem(METRICS_KEY, JSON.stringify(all));
  }
};

// Also persist to Firestore when available
const persistToFirestore = async (event: MonitoringEvent) => {
  if (isDemoMode || !db) return;
  try {
    const { collection: col, addDoc } = await import('firebase/firestore');
    await addDoc(col(db, 'telemetry_events'), {
      ...event,
      timestamp: new Date(event.timestamp),
    });
  } catch {
    // Silently fail — don't recurse into error tracking
  }
};

const persistMetricsToFirestore = async (metrics: PerformanceMetrics) => {
  if (isDemoMode || !db) return;
  try {
    const { collection: col, addDoc } = await import('firebase/firestore');
    await addDoc(col(db, 'telemetry_metrics'), {
      ...metrics,
      timestamp: new Date(metrics.timestamp),
    });
  } catch {
    // Silently fail
  }
};

// ── Breadcrumb management ──

const addBreadcrumb = (crumb: Omit<Breadcrumb, 'timestamp'>) => {
  _breadcrumbs.push({ ...crumb, timestamp: Date.now() });
  if (_breadcrumbs.length > MAX_BREADCRUMBS) _breadcrumbs.shift();
};

// ── Core API ──

export const monitoring = {
  /**
   * Initialize global error handlers and performance observers.
   * Call once at app startup.
   */
  init() {
    if (_initialized) return;
    _initialized = true;

    // Global error handler
    window.addEventListener('error', (event) => {
      monitoring.captureError(event.error || new Error(event.message), {
        source: event.filename,
        line: event.lineno,
        col: event.colno,
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));
      monitoring.captureError(error, { unhandledRejection: true });
    });

    // Performance observer for Core Web Vitals
    if ('PerformanceObserver' in window) {
      try {
        // LCP
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
          if (last) {
            monitoring.recordPerformance({ lcp: last.startTime });
          }
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

        // FCP
        const fcpObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === 'first-contentful-paint') {
              monitoring.recordPerformance({ fcp: entry.startTime });
            }
          }
        });
        fcpObserver.observe({ type: 'paint', buffered: true });

        // CLS
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const layoutShift = entry as PerformanceEntry & { hadRecentInput: boolean; value: number };
            if (!layoutShift.hadRecentInput) {
              clsValue += layoutShift.value;
            }
          }
          monitoring.recordPerformance({ cls: clsValue });
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });
      } catch {
        // PerformanceObserver not fully supported
      }
    }

    // Page load timing
    window.addEventListener('load', () => {
      setTimeout(() => {
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (nav) {
          monitoring.recordPerformance({
            ttfb: nav.responseStart - nav.requestStart,
            pageLoad: nav.loadEventEnd - nav.startTime,
          });
        }
      }, 0);
    });

    // Track navigation via History API
    const originalPushState = history.pushState.bind(history);
    history.pushState = (...args) => {
      originalPushState(...args);
      addBreadcrumb({ type: 'navigation', message: `Navigate to ${location.pathname}` });
      monitoring.logAction('navigation', `Navigate to ${location.pathname}`);
    };
    window.addEventListener('popstate', () => {
      addBreadcrumb({ type: 'navigation', message: `Back/forward to ${location.pathname}` });
      monitoring.logAction('navigation', `Back/forward to ${location.pathname}`);
    });
  },

  /** Set user context (call on login/auth change) */
  setUser(user: { uid: string; email: string; role: string } | null) {
    _userContext = user;
  },

  /** Capture and record an error */
  captureError(
    error: Error | string,
    meta?: Record<string, unknown>,
    severity: MonitoringEventSeverity = 'error',
  ) {
    const err = typeof error === 'string' ? new Error(error) : error;
    const event: MonitoringEvent = {
      id: generateId(),
      type: 'error',
      severity,
      message: err.message,
      stack: err.stack,
      timestamp: Date.now(),
      user: _userContext,
      meta,
      breadcrumbs: [..._breadcrumbs],
      url: location.href,
    };
    storeEvent(event);
    persistToFirestore(event);
    addBreadcrumb({ type: 'error', message: err.message });
  },

  /** Record a performance metric snapshot */
  recordPerformance(partial: Partial<Omit<PerformanceMetrics, 'timestamp' | 'url'>>) {
    const existing = getStoredMetrics();
    const current = existing[0]?.url === location.href && (Date.now() - existing[0].timestamp) < 30000
      ? existing[0]
      : { timestamp: Date.now(), url: location.href };

    const merged: PerformanceMetrics = { ...current, ...partial, timestamp: Date.now(), url: location.href };

    if (current === existing[0]) {
      existing[0] = merged;
      try { localStorage.setItem(METRICS_KEY, JSON.stringify(existing)); } catch { /* noop */ }
    } else {
      storeMetrics(merged);
    }
    persistMetricsToFirestore(merged);
  },

  /** Log a structured user action */
  logAction(
    action: string,
    detail: string,
    meta?: Record<string, unknown>,
  ) {
    const event: MonitoringEvent = {
      id: generateId(),
      type: action === 'navigation' ? 'navigation' : 'action',
      severity: 'info',
      message: detail,
      timestamp: Date.now(),
      user: _userContext,
      meta: { action, ...meta },
      url: location.href,
    };
    storeEvent(event);
    persistToFirestore(event);
    addBreadcrumb({
      type: action === 'navigation' ? 'navigation' : 'action',
      message: detail,
      meta,
    });
  },

  /**
   * Instrument a service call — returns a wrapper that tracks timing and errors.
   * Usage: const result = await monitoring.instrumentCall('attestation.getAll', () => service.getAll());
   */
  async instrumentCall<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    addBreadcrumb({ type: 'network', message: `Call: ${name}` });
    try {
      const result = await fn();
      const duration = Math.round(performance.now() - start);
      monitoring.logAction('api_call', `${name} completed in ${duration}ms`, { duration, success: true });
      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);
      const err = error instanceof Error ? error : new Error(String(error));
      monitoring.captureError(err, { serviceName: name, duration, success: false });
      throw error;
    }
  },

  // ── Read API (for dashboard) ──

  /** Get all stored events, newest first */
  getEvents(opts?: {
    type?: MonitoringEventType;
    severity?: MonitoringEventSeverity;
    limit?: number;
    since?: number;
  }): MonitoringEvent[] {
    let events = getStoredEvents();
    if (opts?.type) events = events.filter((e) => e.type === opts.type);
    if (opts?.severity) events = events.filter((e) => e.severity === opts.severity);
    if (opts?.since) events = events.filter((e) => e.timestamp >= opts.since!);
    if (opts?.limit) events = events.slice(0, opts.limit);
    return events;
  },

  /** Get stored performance metrics */
  getMetrics(limit = 50): PerformanceMetrics[] {
    return getStoredMetrics().slice(0, limit);
  },

  /** Read the current in-memory breadcrumb trail, oldest to newest */
  getBreadcrumbs(limit = MAX_BREADCRUMBS): Breadcrumb[] {
    return _breadcrumbs.slice(Math.max(_breadcrumbs.length - limit, 0));
  },

  /** Get event counts grouped by type */
  getEventCounts(): Record<MonitoringEventType, number> {
    const events = getStoredEvents();
    return {
      error: events.filter((e) => e.type === 'error').length,
      performance: events.filter((e) => e.type === 'performance').length,
      action: events.filter((e) => e.type === 'action').length,
      navigation: events.filter((e) => e.type === 'navigation').length,
    };
  },

  /** Clear all stored telemetry data */
  clearAll() {
    localStorage.removeItem(EVENTS_KEY);
    localStorage.removeItem(METRICS_KEY);
    _breadcrumbs = [];
  },
};
